// Real OpenType features on the canvas: Canvas 2D has no font-feature-settings, but it honors
// FontFace featureSettings. Each (family, weight, style, features) combination gets its own
// registered font face, loaded on first use; text re-renders once it is ready.
import { setFeatureFontResolver, invalidateTextMetrics, interFile, INTER_WEIGHTS } from '@vellum/core';

// font binaries we can load variants from (the bundled Inter 3.19)
const files = import.meta.glob('../../../node_modules/inter-ui/Inter ?web?/Inter-*.woff2', { query: '?url', import: 'default', eager: true }) as Record<string, string>;
const sources = new Map<string, string>();
for (const w of INTER_WEIGHTS)
  for (const italic of [false, true]) {
    const url = Object.entries(files).find(([p]) => p.endsWith(`/${interFile(w, italic)}.woff2`))?.[1];
    if (url) sources.set(`inter|${w}|${italic}`, url);
  }

const ready = new Map<string, string | null>(); // key → alias (null: loading or failed)
const buffers = new Map<string, Promise<ArrayBuffer>>();

function hash(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
}

export function installFeatureFonts(onReady: () => void) {
  setFeatureFontResolver((family, weight, italic, featureCss) => {
    const src = sources.get(`${family.toLowerCase()}|${weight}|${italic}`);
    if (!src) return null;
    const key = `${family}|${weight}|${italic}|${featureCss}`;
    if (ready.has(key)) return ready.get(key)!;
    ready.set(key, null);
    const alias = `${family}__ff_${hash(featureCss)}`;
    if (!buffers.has(src)) buffers.set(src, fetch(src).then((r) => r.arrayBuffer()));
    void buffers
      .get(src)!
      .then(async (buf) => {
        const face = new FontFace(alias, buf, { weight: String(weight), style: italic ? 'italic' : 'normal', featureSettings: featureCss });
        await face.load();
        document.fonts.add(face);
        ready.set(key, alias);
        invalidateTextMetrics();
        onReady();
      })
      .catch(() => {
        /* keep the plain font */
      });
    return null;
  });
}

/** Uploaded fonts can show OpenType features too. */
export function addFeatureSource(family: string, weight: number, italic: boolean, url: string) {
  sources.set(`${family.toLowerCase()}|${weight}|${italic}`, url);
}
